// src/services/stadiumService.ts

import apiClient from './apiClient';
import { StadiumModel, CreateStadiumRequest, UpdateStadiumRequest } from '../models/StadiumModel';
import { PageResponse } from '../models/PaginationModel';

const BASE_URL = '/api/stadiums';

/**
 * Stadium Service - CRUD for Stadium resources.
 * A team can have multiple stadiums (primary home ground + secondary/historical venues).
 */
export const stadiumService = {

    /**
     * Get all active stadiums with pagination.
     */
    async getAllStadiums(page = 0, size = 20): Promise<PageResponse<StadiumModel>> {
        return apiClient.get<PageResponse<StadiumModel>>(`${BASE_URL}?page=${page}&size=${size}`);
    },

    /**
     * Get a single stadium by its database ID.
     */
    async getById(id: number): Promise<StadiumModel> {
        return apiClient.get<StadiumModel>(`${BASE_URL}/${id}`);
    },

    /**
     * Get all active stadiums for a specific team, sorted: primary first, then alphabetically.
     */
    async getByTeam(teamId: number): Promise<StadiumModel[]> {
        return apiClient.get<StadiumModel[]>(`${BASE_URL}/by-team/${teamId}`);
    },

    /**
     * Get all active stadiums for all teams belonging to a club.
     */
    async getByClub(clubId: number): Promise<StadiumModel[]> {
        return apiClient.get<StadiumModel[]>(`${BASE_URL}/by-club/${clubId}`);
    },

    /**
     * Full-text search on name, short_name, city, nickname.
     */
    async search(query: string): Promise<StadiumModel[]> {
        return apiClient.get<StadiumModel[]>(`${BASE_URL}/search?q=${encodeURIComponent(query)}`);
    },

    /**
     * Create a new stadium (authenticated users only).
     */
    async create(data: CreateStadiumRequest): Promise<StadiumModel> {
        return apiClient.post<StadiumModel>(BASE_URL, data);
    },

    /**
     * Update an existing stadium (authenticated users only).
     * Only provided fields are updated (PATCH semantics on the backend).
     */
    async update(id: number, data: UpdateStadiumRequest): Promise<StadiumModel> {
        return apiClient.put<StadiumModel>(`${BASE_URL}/${id}`, data);
    },

    /**
     * Soft-delete a stadium by ID (authenticated users only).
     */
    async delete(id: number): Promise<void> {
        return apiClient.delete<void>(`${BASE_URL}/${id}`);
    },
};